import { ChangeDetectionStrategy, Component, booleanAttribute, computed, input } from '@angular/core';

import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { ChipTone } from './guarantee-vocabulary';

/**
 * `soft` carries a decision or a state the reader must act on; `outline` carries context that
 * should stay legible without competing for attention. `action` is the one variant that is pressed:
 * a lifted, bordered chip on a white ground, so a control never reads as one more label in the row.
 */
export type ChipVariant = 'soft' | 'outline' | 'action';

const TONE_CLASSES: Record<ChipVariant, Record<ChipTone, string>> = {
  soft: {
    neutral: 'bg-neutral-100 text-neutral-600',
    primary: 'bg-primary-50 text-primary-700',
    success: 'bg-success-50 text-success-700',
    warning: 'bg-warning-50 text-warning-800',
    error: 'bg-error-50 text-error-700',
    info: 'bg-info-50 text-info-700',
  },
  outline: {
    neutral: 'border border-neutral-200 text-neutral-500',
    primary: 'border border-primary-200 text-primary-700',
    success: 'border border-success-200 text-success-700',
    warning: 'border border-warning-200 text-warning-800',
    error: 'border border-error-200 text-error-700',
    info: 'border border-info-200 text-info-700',
  },
  action: {
    neutral: 'border border-neutral-300 bg-neutral-0 text-neutral-700 shadow-xs hover:border-neutral-400 hover:bg-neutral-50',
    primary: 'border border-primary-300 bg-neutral-0 text-primary-700 shadow-xs hover:border-primary-400 hover:bg-primary-50',
    success: 'border border-success-300 bg-neutral-0 text-success-700 shadow-xs hover:border-success-400 hover:bg-success-50',
    warning: 'border border-warning-300 bg-neutral-0 text-warning-800 shadow-xs hover:border-warning-400 hover:bg-warning-50',
    error: 'border border-error-300 bg-neutral-0 text-error-700 shadow-xs hover:border-error-400 hover:bg-error-50',
    info: 'border border-info-300 bg-neutral-0 text-info-700 shadow-xs hover:border-info-400 hover:bg-info-50',
  },
};

/** The single chip vocabulary for this UI — never hand-roll a badge beside it. */
@Component({
  selector: 'guarantees-chip',
  standalone: true,
  imports: [MatIcon, MatTooltip],
  // A custom element defaults to `display: inline`, which would wrap the chip in a line box and
  // leave the host taller than what it draws — enough for a ring on an ancestor to sit off the
  // chip it belongs to, and for the chip to ride high against its neighbours.
  host: { class: 'inline-flex max-w-full' },
  template: `
    <span
      class="inline-flex max-w-full items-center gap-0.5 text-sm font-semibold whitespace-nowrap tabular-nums"
      [class]="chipClass()"
      [matTooltip]="tooltip()"
      [matTooltipDisabled]="!tooltip()">
      @if (icon()) {
        <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">{{ icon() }}</mat-icon>
      }
      <span [class]="labelClass()"><ng-content /></span>
    </span>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChipComponent {
  tone = input<ChipTone>('neutral');
  variant = input<ChipVariant>('soft');
  icon = input<string>();
  tooltip = input<string>('');
  /**
   * Drops the label once the line container is too narrow to seat it, leaving the icon and its
   * tooltip. Only honoured on a chip that has an icon — a chip with neither would read as empty.
   */
  collapsible = input(false, { transform: booleanAttribute });
  /**
   * Never shows the label, at any width. For a chip that qualifies another one rather than standing
   * on its own — a sync state beside a fulfilment state — where two labels in one cell would not fit.
   */
  iconOnly = input(false, { transform: booleanAttribute });

  protected readonly toneClass = computed(() => TONE_CLASSES[this.variant()][this.tone()] ?? TONE_CLASSES.soft.neutral);

  /**
   * The `@max-*` direction is deliberate: a chip rendered with no `@container/line` ancestor keeps
   * its label, where the `@min-*` form would hide it forever.
   */
  private readonly collapses = computed(() => this.collapsible() && !!this.icon());
  private readonly iconic = computed(() => this.iconOnly() && !!this.icon());

  protected readonly labelClass = computed(() => {
    if (this.iconic()) {
      return 'hidden';
    }

    return this.collapses() ? 'truncate @max-7xl/line:hidden' : 'truncate';
  });

  /**
   * Shape before colour, because colour is already spoken for: every tone in the palette is in use
   * naming a state, so a control cannot be told from a label by how it is coloured. Corners can —
   * a pill is a thing being reported, a rounded rectangle is a thing to press, and nothing on the
   * page is both. The taller box and the wider padding are the same argument: a control is a target.
   */
  private readonly shapeClass = computed(() => {
    if (this.variant() === 'action') {
      return 'cursor-pointer rounded-lg px-2 py-1 transition-colors duration-150 motion-reduce:transition-none';
    }

    const padding = this.iconic() ? 'px-1' : this.collapses() ? 'px-1.5 @max-7xl/line:px-1' : 'px-1.5';

    return `rounded-full py-0.5 ${padding}`;
  });

  protected readonly chipClass = computed(() => `${this.toneClass()} ${this.shapeClass()}`);
}
